/**
 * ชื่อไฟล์ในรายการ "อัปโหลดไฟล์" ของประวัติกิจกรรม — กดเปิดดูได้ (feedback 29 ก.ย. 2026 เคส #581)
 *
 * เดิมเป็นข้อความเฉย ๆ ต้องเลื่อนลงไปหาการ์ดไฟล์แนบเอง (หลายไฟล์ชื่อคล้ายกัน line_oa_chat_…)
 * · รูป → เปิดในหน้าต่าง + ปุ่มดาวน์โหลด · PDF → แสดงในหน้าต่าง · ชนิดอื่น → ดาวน์โหลดทันที (เหมือนการ์ดไฟล์แนบ)
 * ดึงผ่าน GET /device-maintenance/{id}/attachments/{attachment_id}/file (maintenance.view — ชุดเดียวกับที่เห็นไทม์ไลน์)
 * ไม่ใช้ window.open หลัง await (เบราว์เซอร์บล็อก popup ที่ไม่ได้เปิดตรงจากการคลิก)
 */
import React, { useEffect, useState } from 'react';
import { RiDownloadLine, RiLoader4Line } from 'react-icons/ri';
import Modal from '../ui/Modal';
import deviceMaintenanceService from '../../services/deviceMaintenanceService';
import { showAlert } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';

interface Props {
  maintenanceId: number;
  attachmentId: number;
  fileName: string;
  fileType?: string | null;
}

type Kind = 'image' | 'pdf' | 'other';

export function attachmentViewKind(fileType?: string | null, fileName?: string): Kind {
  const t = (fileType || '').toLowerCase();
  const ext = (fileName || '').toLowerCase().split('.').pop() || '';
  if (t.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (t === 'application/pdf' || ext === 'pdf') return 'pdf';
  return 'other';
}

const TimelineAttachmentLink: React.FC<Props> = ({ maintenanceId, attachmentId, fileName, fileType }) => {
  const [loading, setLoading] = useState(false);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const kind = attachmentViewKind(fileType, fileName);

  // คืนหน่วยความจำของ object URL เมื่อปิดหน้าต่าง/ออกจากหน้า
  useEffect(() => () => { if (viewUrl) URL.revokeObjectURL(viewUrl); }, [viewUrl]);

  const fetchBlob = async (): Promise<Blob | null> => {
    try {
      return await deviceMaintenanceService.getAttachmentBlob(maintenanceId, attachmentId);
    } catch (error) {
      // blob request → error envelope เป็น Blob ต้องอ่านแบบ async (เหตุผลจริง เช่น 404 ไฟล์ถูกลบ / 403)
      const msg = await getApiErrorMessageAsync(error, 'ไม่สามารถเปิดไฟล์ได้ กรุณาลองใหม่อีกครั้ง');
      showAlert('เกิดข้อผิดพลาด', msg, 'error');
      return null;
    }
  };

  const download = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpen = async () => {
    if (loading) return;
    setLoading(true);
    const blob = await fetchBlob();
    setLoading(false);
    if (!blob) return;
    if (kind === 'other') {
      download(blob);
      return;
    }
    // PDF ต้องมี type ถูก ไม่งั้น iframe แสดงเป็นข้อความดิบ/ดาวน์โหลดแทน
    const typed = kind === 'pdf' && blob.type !== 'application/pdf' ? new Blob([blob], { type: 'application/pdf' }) : blob;
    setViewUrl(URL.createObjectURL(typed));
  };

  const handleDownloadFromViewer = async () => {
    const blob = await fetchBlob();
    if (blob) download(blob);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        disabled={loading}
        className="ml-1 inline-flex items-center gap-1 text-primary-700 hover:text-primary-800 underline underline-offset-2 disabled:opacity-60 text-left break-all"
        title={kind === 'other' ? 'ดาวน์โหลดไฟล์' : 'เปิดดูไฟล์'}
        data-testid="timeline-attachment-open"
      >
        {fileName}
        {loading && <RiLoader4Line className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
      </button>
      <Modal
        isOpen={viewUrl !== null}
        onClose={() => setViewUrl(null)}
        title={fileName}
        size="xl"
        footer={
          <>
            <button
              type="button"
              onClick={handleDownloadFromViewer}
              className="inline-flex items-center gap-2 px-4 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              <RiDownloadLine className="h-4 w-4" />
              ดาวน์โหลด
            </button>
            <button
              type="button"
              onClick={() => setViewUrl(null)}
              className="inline-flex items-center gap-2 px-4 min-h-[44px] sm:min-h-[38px] py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50"
            >
              ปิด
            </button>
          </>
        }
      >
        {viewUrl && kind === 'image' && (
          <div className="flex items-center justify-center">
            <img src={viewUrl} alt={fileName} className="max-h-[70vh] max-w-full object-contain rounded" />
          </div>
        )}
        {viewUrl && kind === 'pdf' && (
          <iframe src={viewUrl} title={fileName} className="w-full h-[70vh] rounded border border-secondary-200" />
        )}
      </Modal>
    </>
  );
};

export default TimelineAttachmentLink;
